/**
 * The searchers behind every picker in the app.
 *
 * Each searcher has a matching **resolver**. A form that opens on a saved value holds an id and
 * nothing else, and an id is not a name — showing one is the difference between "Mrs Folake
 * Adeyemi" and "01M0D608FSG69TKG81N718Q9". The resolver asks the same endpoint for that one
 * record, so the picker can label itself before the user touches it.
 *
 * One place, so the same query is not written three ways in three screens, and so the limit and
 * the shape of a result row are decided once. Each returns the `Option` shape `SearchSelect`
 * expects.
 */

import { COUNTRIES, cycleSuffix, searchCountries, type RentCycle } from "@rentorng/core";
import { api } from "./api";
import { PropertyTypeIcon, isResidentialType } from "@/components/property-icons";
import type { Option } from "@/components/search-select";

/** The mark a picker row shows, sized for a two-line option. */
function typeIcon(types?: readonly string[] | string | null) {
  const type = Array.isArray(types) ? types[0] : types;
  if (!type) return undefined;
  return (
    <PropertyTypeIcon
      type={type}
      size={17}
      className={isResidentialType(type) ? "text-brand-600" : "text-info-600"}
    />
  );
}

interface PartyRow {
  id: string;
  name: string;
  phone: string | null;
  email?: string | null;
}

/** Clients, optionally narrowed to one role. */
export function searchParties(role?: string) {
  return async (query: string): Promise<Option[]> => {
    const data = await api.get<{ parties: PartyRow[] }>("/parties/lookup", { role, q: query });
    return data.parties.map((party) => ({
      value: party.id,
      label: party.name,
      hint: party.phone ?? party.email ?? undefined,
    }));
  };
}

/** One client by id, for a picker opening on a saved value. */
export async function resolveParty(id: string): Promise<Option | null> {
  if (!id) return null;
  const data = await api.get<{ parties: PartyRow[] }>("/parties/lookup", { id });
  const party = data.parties[0];
  return party ? { value: party.id, label: party.name, hint: party.phone ?? party.email ?? undefined } : null;
}

interface UnitRow {
  id: string;
  label: string;
  propertyName: string;
  propertyTypes?: string[] | null;
  status?: string;
  rentAmount?: number | null;
  rentCycle?: string;
}

/** Units that can take a new tenancy — vacant, reserved or on notice. */
export async function searchVacantUnits(query: string): Promise<Option[]> {
  const data = await api.get<{ units: UnitRow[] }>("/units/vacant", { q: query });
  return data.units.map((unit) => ({
    value: unit.id,
    label: `${unit.propertyName} · ${unit.label}`,
    hint: unit.rentAmount ? `${(unit.rentAmount / 100).toLocaleString()} ${cycleSuffix((unit.rentCycle ?? "annual") as RentCycle)}` : undefined,
    icon: typeIcon(unit.propertyTypes),
  }));
}

/** Every unit, whatever its status. */
export async function searchUnits(query: string): Promise<Option[]> {
  const data = await api.get<{ units: UnitRow[] }>("/units/lookup", { q: query });
  return data.units.map((unit) => ({
    value: unit.id,
    label: `${unit.propertyName} · ${unit.label}`,
    tag: unit.status?.replace(/_/g, " "),
    icon: typeIcon(unit.propertyTypes),
  }));
}

/** One unit by id, whatever its status — the unit on a saved record is rarely still vacant. */
export async function resolveUnit(id: string): Promise<Option | null> {
  if (!id) return null;
  const data = await api.get<{ units: UnitRow[] }>("/units/lookup", { id });
  const unit = data.units[0];
  return unit
    ? {
        value: unit.id,
        label: `${unit.propertyName} · ${unit.label}`,
        tag: unit.status?.replace(/_/g, " "),
        icon: typeIcon(unit.propertyTypes),
      }
    : null;
}

export async function searchProperties(query: string): Promise<Option[]> {
  const data = await api.get<{ properties: Array<{ id: string; name: string; city: string | null; types?: string[] }> }>(
    "/properties/lookup",
    { q: query },
  );
  return data.properties.map((property) => ({
    value: property.id,
    label: property.name,
    hint: property.city ?? undefined,
    icon: typeIcon(property.types),
  }));
}

export async function resolveProperty(id: string): Promise<Option | null> {
  if (!id) return null;
  const data = await api.get<{ properties: Array<{ id: string; name: string; city: string | null; types?: string[] }> }>(
    "/properties/lookup",
    { id },
  );
  const property = data.properties[0];
  return property
    ? { value: property.id, label: property.name, hint: property.city ?? undefined, icon: typeIcon(property.types) }
    : null;
}

/**
 * Development schemes, for the screens that are shared between the two modules.
 *
 * There is no `/estates/lookup` to match `/properties/lookup`, and one was not added: the list
 * endpoint already takes `q`, already applies `developmentFilter`, and a second endpoint answering
 * the same question is a second place for the confinement rule to be forgotten. `perPage` is
 * capped here rather than left to the endpoint's default, because a picker shows a dozen rows and
 * fetching a page of forty to draw twelve is a cost paid on every keystroke.
 */
export async function searchEstates(query: string): Promise<Option[]> {
  const data = await api.get<{ estates: Array<{ id: string; name: string; city: string | null }> }>("/estates", {
    q: query,
    perPage: 12,
  });
  return data.estates.map((estate) => ({
    value: estate.id,
    label: estate.name,
    hint: estate.city ?? undefined,
  }));
}

/** One scheme by id, so a picker opening on a saved value shows a name rather than an id. */
export async function resolveEstate(id: string): Promise<Option | null> {
  if (!id) return null;
  try {
    const data = await api.get<{ estate: { id: string; name: string; city: string | null } }>(`/estates/${id}`);
    return data.estate ? { value: data.estate.id, label: data.estate.name, hint: data.estate.city ?? undefined } : null;
  } catch {
    /* A scheme that has since been deleted leaves a cost behind pointing at nothing. Showing the
       picker empty is better than failing the form it sits in. */
    return null;
  }
}

/**
 * Staff. Small enough to fetch whole and filter in the browser — an agency with more than a
 * couple of dozen logins is not the common case, and the list is already loaded elsewhere.
 */
export async function searchStaff(query: string): Promise<Option[]> {
  const data = await api.get<{ staff: Array<{ id: string; name: string; email: string; role: string }> }>(
    "/properties/lookup",
  );
  const needle = query.trim().toLowerCase();
  return data.staff
    .filter((member) => !needle || member.name.toLowerCase().includes(needle) || member.email.toLowerCase().includes(needle))
    .map((member) => ({ value: member.id, label: member.name, hint: member.email, tag: member.role }));
}

/** One staff member by id. The list is small, so this is a filter rather than a second request. */
export async function resolveStaff(id: string): Promise<Option | null> {
  if (!id) return null;
  const all = await searchStaff("");
  return all.find((member) => member.value === id) ?? null;
}

/** Countries are a fixed list, so both halves are local. */
export async function searchCountryOptions(query: string): Promise<Option[]> {
  return searchCountries(query).map((country) => ({ value: country.name, label: country.name, tag: country.code }));
}

export async function resolveCountry(name: string): Promise<Option | null> {
  if (!name) return null;
  const known = COUNTRIES.find((country) => country.name.toLowerCase() === name.toLowerCase());
  // A country typed in before the list existed still shows what it says, rather than vanishing.
  return known ? { value: known.name, label: known.name, tag: known.code } : { value: name, label: name };
}
